import type { Map } from 'immutable';
import Immutable from 'immutable';
import type {
    RawSceneData,
    RawVideoData,
    Version,
    VersionInfo,
    RawTemplateData,
    OpaqueVideoData,
} from '../types';
import type { TemplateBundle } from '../../types';
import { ALL_ASPECT_RATIOS } from '../../aspectRatioConfig';
import type { AspectRatio } from '../../aspectRatioConfig';
import { migrationTo9_224 } from './migrator-to-9-224';
import { migrationTo9_225 } from './migrator-to-9-225';
import { migrationTo9_226 } from './migrator-to-9-226';
import { migrationTo9_227 } from './migrator-to-9-227';
import { migrationTo9_229 } from './migrator-to-9-229';
import type { Migration } from './types';
import { migrationTo9_228 } from './migrator-to-9-228';

type MigrationOptions = {
    /** Desired version to migrate to. */
    version?: Version;
};

/**
 * Any incoming data to the migrator must always contain these properties.
 */
type RequiredMigratorData = {
    version: Version;
};

/**
 * Latest migration version.
 *
 * NOTES ON VIDEO_DATA VERSIONING
 * We version the Config Schema to track changes and to ensure we've updated
 * all video_data JSON objects in use (any potentially in the frontend as well as the database).
 * Versioning uses Semantic Versioning - please ensure to update the version when making changes
 * to the Serializer directly or any of the helpers.
 * Please add a description of any changes to CHANGELOG.
 */
export const VERSION: Version = '9.229.0';

/**
 * This is the version where "version" was added to template and scene data.
 * This value should not be updated, it is used in migration to determine when to apply versions, i.e. not before
 * reaching 9.113.0. See `updateVideoData`.
 */
const VERSIONING_INTRODUCTION: Version = '9.113.0';

/**
 * Upgrades `video_data`, `template_data`, `scene_design_data` to the latest version (`VERSION`).
 * The minimum major `video_data` version supported by this migrator is 9.61 – anything lower than that will fail.
 *
 * ```js
 *
 * // LIBS
 * import { Migrator, VERSION } from '../../src/video-data/migrations/migrator';
 *
 * const updated_video_data = Migrator.updateVideoData(video_data);
 * ```
 */
export class Migrator {
    /**
     * Parse string version into object.
     * @param version version string
     * @returns parsed version object
     */
    static _parseVersion(version: Version): VersionInfo {
        const split_version = version.split('.');
        return {
            major: parseInt(split_version[0]),
            minor: parseInt(split_version[1]),
            patch: parseInt(split_version[2]),
        };
    }

    /**
     * @returns 0 if v1 == v2, 1 if v1 > v2 and -1 if v1 < v2. Similar idea to sign(v1 - v2) if v1 and v2 were numbers.
     */
    private static _compareVersion(v1: VersionInfo, v2: VersionInfo) {
        if (v1.major === v2.major && v1.minor === v2.minor && v1.patch === v2.patch) {
            return 0;
        }

        if (
            v1.major > v2.major ||
            (v1.major === v2.major && v1.minor > v2.minor) ||
            (v1.major === v2.major && v1.minor === v2.minor && v1.patch > v2.patch)
        ) {
            return 1;
        }

        return -1;
    }

    /**
     * Checks if the `version` passed in is ahead of the latest version (`VERSION`).
     *
     * @param currentVersion - a version that we should check.
     * @param desiredVersion - the desired version that we should migrate to.
     * @returns `true` if the version passed in is ahead of the desired version, `false` otherwise.
     */
    static isVersionAhead(currentVersion: Version, desiredVersion: Version): boolean {
        const parsedCurrentVersion = Migrator._parseVersion(currentVersion);
        const parsedDesiredVersion = Migrator._parseVersion(desiredVersion);
        return Migrator._compareVersion(parsedCurrentVersion, parsedDesiredVersion) > 0;
    }

    static updateVideoData(data: OpaqueVideoData, options?: MigrationOptions): RawVideoData {
        const migratedData = Migrator._migrate<RawVideoData>(
            data,
            (migration: Migration, data: Immutable.Map<string, any>) =>
                migration.migrateVideo(data),
            options,
        );

        // Update the template and scene data version, as they are migrated implicitly via calls from migrateVideo, and
        // they won't have their version updated to the video data version at the end of `Migrator.migrate`.
        const parsedTargetVersion = Migrator._parseVersion(VERSIONING_INTRODUCTION);
        const migratedVersion = migratedData.version;
        const parsedMigratedVersion = Migrator._parseVersion(migratedVersion);
        if (Migrator._compareVersion(parsedMigratedVersion, parsedTargetVersion) >= 0) {
            migratedData.template.version = migratedVersion;
            migratedData.slides.forEach((scene) => {
                scene.version = migratedVersion;
            });
        }

        return migratedData;
    }

    /**
     * We deliberately update the passed templateBundle in place to avoid creating a new object and break external
     * references.
     */
    static updateTemplateBundle(
        templateBundle: TemplateBundle,
        options?: MigrationOptions,
    ): TemplateBundle {
        const migratedSceneDesigns = {} as Record<string, Record<AspectRatio, OpaqueVideoData>>;

        Object.keys(templateBundle.sceneDesigns).forEach((sceneDesignId) => {
            const sceneDesign = templateBundle.sceneDesigns[sceneDesignId];
            const migratedSceneDesign = {} as Record<AspectRatio, OpaqueVideoData>;

            ALL_ASPECT_RATIOS.forEach((aspectRatio: AspectRatio) => {
                const sceneDesignData = sceneDesign[aspectRatio];
                if (!sceneDesignData) return;

                migratedSceneDesign[aspectRatio as AspectRatio] = Migrator.updateSceneDesignData(
                    sceneDesignData,
                    options,
                );
            });

            migratedSceneDesigns[sceneDesignId] = migratedSceneDesign;
        });

        return {
            templateData: Migrator.updateTemplateData(templateBundle.templateData, options),
            sceneDesigns: migratedSceneDesigns,
        };
    }

    static updateTemplateData(data: OpaqueVideoData, options?: MigrationOptions): RawTemplateData {
        return Migrator._migrate(
            data,
            (migration: Migration, data: Immutable.Map<string, any>) =>
                migration.migrateTemplate(data),
            options,
        );
    }

    static updateSceneData(data: OpaqueVideoData, options?: MigrationOptions): RawSceneData {
        return Migrator._migrate(
            data,
            (migration: Migration, data: Immutable.Map<string, any>) =>
                migration.migrateScene(data, false),
            options,
        );
    }

    static updateSceneDesignData(data: OpaqueVideoData, options?: MigrationOptions): RawSceneData {
        return Migrator._migrate(
            data,
            (migration: Migration, data: Immutable.Map<string, any>) =>
                migration.migrateScene(data, true),
            options,
        );
    }

    static #assertVersion(version: Version | undefined) {
        if (version === undefined) {
            throw new Error('unable to migrate because missing `version`');
        }
        if (version.match(/\./g).length !== 2) {
            throw new Error(`invalid version: ${version}`);
        }
    }

    /**
     * Goes through all migrations and migrate data step by step till desired version is reached.
     * If no desired version is passed, it will migrate till latest version.
     * @internal
     */
    static _migrate<T>(
        data: OpaqueVideoData,
        doMigrationCallback: (
            migration: Migration,
            data: Immutable.Map<string, any>,
        ) => Immutable.Map<string, any>,
        options?: MigrationOptions,
    ): T {
        // Ensure that the opaque data object matches the required migrator data interface.
        const currentVersion: Version = (data as RequiredMigratorData).version;
        Migrator.#assertVersion(currentVersion);

        const desiredVersion = options?.version ?? VERSION;
        if (currentVersion === desiredVersion) return data as T;

        // The migrator uses Immutable to prevent object mutations from interacting within a migration
        // or between different migrations. Refer to LU-1648 for more info.
        const immutableData = (() => {
            let result = Immutable.fromJS(data) as unknown as Map<string, unknown>;
            let version = result.get('version') as Version;

            while (version !== desiredVersion) {
                const migration = this._migration_steps[version];

                if (migration) {
                    if (migration.migration) {
                        result = doMigrationCallback(migration.migration, result);
                    }

                    version = migration.versionMigratingTo;
                    continue;
                }

                if (Migrator.isVersionAhead(version, VERSION)) {
                    console.warn(
                        "video's version is AHEAD of current version, apply safe downgrade of latest version",
                    );
                    return result.set('version', VERSION);
                }

                throw new Error(`could not find migration to upgrade from version ${version}`);
            }

            return result.set('version', desiredVersion);
        })();

        // Convert back to a plain JS object
        return immutableData.toJS() as T;
    }

    /**
     * Connects different versions together by telling the system what `operation` it needs to run
     * to transform a video at version A (`versionMigratingFrom`) to version B (`versionMigratingTo`).
     * @key is a video's version before migration. This is a dynamic parameter.
     */
    static _migration_steps: Record<
        string,
        {
            /**
             * action to take: null (do nothing) OR method that takes `data` and returns updated `data`.
             */
            migration: Migration;
            /**
             *  video's version to upgrade (migrate) to.
             */
            versionMigratingTo: Version;
        }
    > = {
        '9.223.0': {
            migration: migrationTo9_224,
            versionMigratingTo: '9.224.0',
        },
        '9.224.0': {
            migration: migrationTo9_225,
            versionMigratingTo: '9.225.0',
        },
        '9.225.0': {
            migration: migrationTo9_226,
            versionMigratingTo: '9.226.0',
        },
        '9.226.0': {
            migration: migrationTo9_227,
            versionMigratingTo: '9.227.0',
        },
        '9.227.0': {
            migration: migrationTo9_228,
            versionMigratingTo: '9.228.0',
        },
        '9.228.0': {
            migration: migrationTo9_229,
            versionMigratingTo: '9.229.0',
        },
        // STOP! making a new migration? you should use our new-migration.py script.
        // Details here: https://www.notion.so/lumen5/Luminary-Migrations-4d875431a099483fadf28b2fb54ccfc8#98caa23dc53c492aa0612162c3e780ef
    };
}
